import type { OrderStatus, OrderView, ProviderPaymentStatus, QuoteKind, RefundStatus } from "@townsy/contracts";

/**
 * Presentation of the order lifecycle (docs/04 section 8, docs/01 section 6 "Purchase review"
 * states). Labels are short headlines; the server's `OrderView.message` is always shown verbatim
 * next to them. Nothing here decides an outcome: a payment is "complete" only when the server's
 * order says `fulfilled`, and a refund is "refunded" only when the server says so.
 */

/** The order will not change by itself any more (from the buyer's point of view). */
export const SETTLED_STATUSES: ReadonlySet<OrderStatus> = new Set(["fulfilled", "expired", "canceled", "refunded", "manual_review"]);

/** Money is on its way back to the card: still moving, but the purchase itself is over. */
export const REFUND_IN_PROGRESS: ReadonlySet<OrderStatus> = new Set(["compensation_required", "refund_pending"]);

/** The buyer can still pay (card shortfall) while the hold lasts. */
export const PAYABLE_STATUSES: ReadonlySet<OrderStatus> = new Set(["created", "reserved", "awaiting_payment"]);

/** A cancellation can still be requested (the provider's actual state decides). */
export const CANCELABLE_STATUSES: ReadonlySet<OrderStatus> = new Set(["created", "reserved", "awaiting_payment"]);

export const isSettled = (s: OrderStatus) => SETTLED_STATUSES.has(s);
export const isRefundInProgress = (s: OrderStatus) => REFUND_IN_PROGRESS.has(s);

export type OrderTone = "info" | "success" | "warning" | "danger";

export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  created: "Preparing",
  reserved: "Held for you",
  awaiting_payment: "Waiting for your card payment",
  payment_confirmed: "Payment received — finishing up",
  fulfilled: "Purchase complete",
  expired: "Hold expired",
  canceled: "Canceled",
  compensation_required: "Refund to your card required",
  refund_pending: "Refund pending",
  refunded: "Refunded to your card",
  manual_review: "Support review required",
};

export const ORDER_STATUS_TONE: Record<OrderStatus, OrderTone> = {
  created: "info",
  reserved: "info",
  awaiting_payment: "info",
  payment_confirmed: "info",
  fulfilled: "success",
  expired: "warning",
  canceled: "warning",
  compensation_required: "warning",
  refund_pending: "warning",
  refunded: "info",
  manual_review: "danger",
};

/** `requires_capture` does not exist in the contract: anything the server cannot map is `unknown`. */
export const PAYMENT_STATUS_LABEL: Record<ProviderPaymentStatus, string> = {
  none: "Not started yet",
  requires_payment_method: "Waiting for card details",
  requires_confirmation: "Waiting for confirmation",
  requires_action: "Waiting for card authentication",
  processing: "Processing",
  succeeded: "Card payment succeeded",
  canceled: "Card payment canceled",
  unknown: "Unknown — Townsy is checking with the payment provider",
};

export const REFUND_STATUS_LABEL: Record<RefundStatus, string> = {
  none: "No refund",
  pending: "Refund pending",
  requires_action: "Refund needs an extra step (support alerted)",
  succeeded: "Refund completed",
  failed: "Refund failed (support alerted)",
  canceled: "Refund canceled (support alerted)",
};

export const KIND_LABEL: Record<QuoteKind, string> = {
  COMMERCIAL_ACQUISITION: "Commercial building",
  RESIDENTIAL_PLOT: "Residential plot",
  HOME_UPGRADE: "Home upgrade",
  CATALOG_ITEM: "Home item",
  BILLBOARD_RENTAL: "Billboard rental",
  /** M6: card funding of a sponsored-task campaign budget (card only; never Townsy Credit). */
  CAMPAIGN_FUNDING: "Sponsored task budget",
};

/** Order kinds the card pays in full: the receipt never shows a Townsy Credit option for them. */
export const CARD_ONLY_KINDS: ReadonlySet<QuoteKind> = new Set(["CAMPAIGN_FUNDING"]);

/** Funding lines for a receipt: always three separate amounts (docs/04 section 1). */
export function fundingLines(o: Pick<OrderView, "subtotalCents" | "taxCents" | "creditAppliedCents" | "cardAmountCents">) {
  return {
    subtotalCents: o.subtotalCents,
    taxCents: o.taxCents,
    creditCents: o.creditAppliedCents,
    cardCents: o.cardAmountCents,
    totalCents: o.subtotalCents + o.taxCents,
  };
}

/**
 * Polling cadence while waiting for the server (webhooks or the reconciliation job decide). Starts
 * fast right after a card confirmation, then backs off; refunds move slowly, so they are polled
 * less often. Returns false when polling should stop.
 */
export function orderPollDelayMs(status: OrderStatus, attempt: number): number | false {
  if (isSettled(status)) return false;
  if (isRefundInProgress(status)) return 10_000;
  const steps = [1000, 1500, 2000, 2500, 3000, 4000];
  return steps[Math.min(attempt, steps.length - 1)] ?? 5000;
}

/** Polling of one order inside the checkout stops after this long; the order page keeps going. */
export const CHECKOUT_POLL_LIMIT_MS = 3 * 60_000;

/** True while this order still holds the property / credit for the buyer and needs a card payment. */
export function canPayNow(o: Pick<OrderView, "status" | "cardAmountCents" | "reservationExpiresAt">, now: number): boolean {
  if (!PAYABLE_STATUSES.has(o.status) || o.cardAmountCents <= 0) return false;
  if (!o.reservationExpiresAt) return false;
  const t = Date.parse(o.reservationExpiresAt);
  return Number.isFinite(t) && t > now;
}

export function canCancel(o: Pick<OrderView, "status">): boolean {
  return CANCELABLE_STATUSES.has(o.status);
}
